import { WebsocketChannelEnum } from "./model/shared";
import { GetOrderbook200Response } from "../../codegen-api";
import {
  MarketDecimals,
  convertOrderbookPrice,
  convertOrderbookQuantity,
} from "../../utils/rawConversion";
import { normalizeDecimalString, toPlainDecimalString } from "../../utils/decimalString";
import { toMillis } from "../../utils/timestampUnits";
import { Overlay, SideOverlay } from "./useOrderbookWSS.types";

export const getOrderbookChannel = (instrument: string) =>
  `${WebsocketChannelEnum.ORDER_BOOK}:${instrument}`;

export const getTradesChannel = (instrument: string) => `trades:${instrument}`;

/**
 * Normalize orderbook data format and convert raw integers to decimals.
 *
 * Decimals are a REQUIRED argument rather than a registry lookup: this used to
 * fall back to `{18, 6}` whenever the market was not registered yet, silently
 * scaling an 18/18 perp book by 10^12 and never re-converting it. Returns
 * `undefined` when the decimals are unknown, so callers show a pending book
 * instead of a wrong one.
 */
export const normalizeOrderbookFormat = (
  data: GetOrderbook200Response | undefined,
  decimals: MarketDecimals | undefined
): GetOrderbook200Response | undefined => {
  if (!data || !decimals) {
    return undefined;
  }

  const { baseDecimals, quoteDecimals } = decimals;

  // Always convert — backend always sends raw integers
  const cvPrice = (val: string | number): string =>
    convertOrderbookPrice(val, baseDecimals, quoteDecimals);

  const cvQty = (val: string | number): string =>
    convertOrderbookQuantity(val, baseDecimals);

  const normalizeLevel = (level: any) => {
    if (!level) return [];
    if (Array.isArray(level[0])) {
      return level.map(([price, amount]: [string, string]) => [
        cvPrice(price),
        cvQty(amount),
      ]);
    }
    if (typeof level[0] === "object" && "price" in level[0]) {
      return level.map((item: any) => [
        cvPrice(item.price),
        cvQty(item.amount ?? item.quantity ?? item.size ?? "0"),
      ]);
    }
    return level;
  };

  return {
    ...data,
    bids: normalizeLevel(data.bids),
    asks: normalizeLevel(data.asks),
  };
};

export const isIndexerOrderEvent = (data: any): data is { action: string; order: any } =>
  !!data &&
  typeof data === "object" &&
  typeof data.action === "string" &&
  data.order &&
  typeof data.order === "object" &&
  typeof data.order.price === "string";

export const newOverlay = (): Overlay => ({ bids: new Map(), asks: new Map() });

/**
 * The identity of a price level, as canonical plain decimal text.
 *
 * This used to be `Number(price).toFixed(8)`, which had two faults that
 * compounded: every price below 5e-9 keyed to "0.00000000", so unrelated levels
 * MERGED; and the level's price was then rebuilt from the key, so an
 * 18-decimal price lost its tail on every composition. The merged level came
 * back out priced "0" -- and a zero-priced level is not a cosmetic problem. It
 * sorts to the touch (levels are ordered by `Number(price)`), `isCrossedBook`
 * reads it against the real side, and it used to make `sizeForUsdAgainstBook`
 * hand out its entire size for free.
 *
 * `normalizeDecimalString` expands the string without going through a float, so
 * distinct prices keep distinct keys and the key IS the exact price.
 */
export const priceKey = (priceDisplay: string): string =>
  normalizeDecimalString(priceDisplay) ?? priceDisplay;

export const ORDERBOOK_EVENT_CACHE_LIMIT = 1000;

const getFirstPresent = (source: any, keys: string[]) => {
  if (!source || typeof source !== "object") return undefined;
  const key = keys.find(
    (candidate) => source[candidate] !== undefined && source[candidate] !== null
  );
  return key ? source[key] : undefined;
};

export const buildOrderbookEventKey = (
  channel: string,
  data: { action: string; order: any }
) => {
  const { action, order } = data;
  const normalizedChannel = channel.toLowerCase();
  const orderId =
    getFirstPresent(order, ["orderId", "order_id", "id"]) ??
    getFirstPresent(data, ["orderId", "order_id", "id"]);

  if (orderId !== undefined) {
    return [normalizedChannel, action, orderId].map(String).join("|");
  }

  const fallbackParts = [
    normalizedChannel,
    action,
    getFirstPresent(order, ["transactionHash", "txHash", "tx_hash"]),
    getFirstPresent(order, ["blockNumber", "block_number"]),
    getFirstPresent(order, ["logIndex", "log_index", "eventIndex", "event_index"]),
    order.market,
    order.trader,
    order.side,
    order.status,
    order.price,
    order.quantity,
    order.filled,
  ];

  return fallbackParts
    .map((part) => (part === undefined || part === null ? "" : String(part)))
    .join("|");
};

// ── Book composition and freshness (FE-10, FE-16) ───────────────────────────

/**
 * Identity of one `trades:` frame. The backend replays its recent trades
 * periodically, so only an id not seen before is evidence the book moved.
 * `undefined` when the frame carries neither an id nor a transaction.
 */
export const getTradeFrameId = (data: unknown): string | undefined => {
  const trade = data && typeof data === "object" ? (data as any) : undefined;
  if (!trade) return undefined;
  const id = trade.trade_id ?? trade.id ?? trade.tradeId;
  if (id !== undefined && id !== null && id !== "") return String(id);
  const tx = trade.transaction_id ?? trade.transactionId;
  if (!tx) return undefined;
  return [
    tx,
    trade.price ?? trade.executionPrice ?? "",
    trade.amount ?? trade.quantity ?? "",
    trade.created_timestamp ?? trade.timestamp ?? "",
    trade.side ?? "",
  ].join(":");
};

/** When a snapshot describes the book: its own clock when it has one, and when we received it. */
export type OrderbookStamp = { sourceMs?: number; receivedMs: number };

/** Past this age a held snapshot no longer blocks a newer-arriving one, whatever its own clock says. */
export const ORDERBOOK_STAMP_MAX_AGE_MS = 10_000;

export const stampOrderbook = (book: unknown, receivedMs: number): OrderbookStamp => {
  const record = book && typeof book === "object" ? (book as any) : undefined;
  // REST /orderbook sends `last_updated_timestamp`; websocket snapshots send `timestamp`.
  const sourceMs = toMillis(
    record?.last_updated_timestamp ??
      record?.last_updated ??
      record?.lastUpdated ??
      record?.timestamp
  );
  return {
    sourceMs: sourceMs !== undefined && sourceMs > 0 ? sourceMs : undefined,
    receivedMs,
  };
};

/**
 * Whether `candidate` may replace the book described by `current`.
 *
 * A REST response can be answered with data from before the websocket
 * snapshot already on screen, and used to overwrite it. Both clocks are the
 * server's when both snapshots carry a timestamp; otherwise arrival order
 * decides. A held snapshot older than ORDERBOOK_STAMP_MAX_AGE_MS never blocks
 * a fresh arrival, so a socket that went quiet cannot freeze the book.
 */
export const isNotOlderBook = (
  candidate: OrderbookStamp,
  current: OrderbookStamp | undefined
) => {
  if (!current) return true;
  if (candidate.receivedMs - current.receivedMs > ORDERBOOK_STAMP_MAX_AGE_MS) {
    return true;
  }
  if (candidate.sourceMs !== undefined && current.sourceMs !== undefined) {
    return candidate.sourceMs >= current.sourceMs;
  }
  return candidate.receivedMs >= current.receivedMs;
};

/** True when the best bid is at or above the best ask. */
export const isCrossedBook = (
  bids: Array<Array<string>> | undefined,
  asks: Array<Array<string>> | undefined
) => {
  if (!bids?.length || !asks?.length) return false;
  const bidPrices = bids.map(([p]) => Number(p)).filter(Number.isFinite);
  const askPrices = asks.map(([p]) => Number(p)).filter(Number.isFinite);
  if (!bidPrices.length || !askPrices.length) return false;
  return Math.max(...bidPrices) >= Math.min(...askPrices);
};

const hasOverlay = (overlay: Overlay) =>
  overlay.bids.size > 0 || overlay.asks.size > 0;

const sumLevels = (
  base: Array<Array<string>> | undefined,
  delta: SideOverlay
): Array<Array<string>> => {
  const out = new Map<string, number>();
  (base || []).forEach(([p, q]) => {
    const k = priceKey(p);
    out.set(k, (out.get(k) || 0) + Number(q));
  });
  delta.forEach((dq, k) => {
    out.set(k, (out.get(k) || 0) + dq);
  });
  const levels: Array<Array<string>> = [];
  out.forEach((q, k) => {
    // Never exponent notation: String(1e-7) is "1e-7".
    // The key is already canonical plain decimal text, so emit it as-is: round
    // -tripping it through `Number` is what truncated an 18-decimal price.
    if (q > 1e-12) levels.push([k, toPlainDecimalString(q)]);
  });
  return levels;
};

export interface ComposedOrderbook {
  book: GetOrderbook200Response | undefined;
  /** Still crossed after composition; the caller should refetch. */
  crossed: boolean;
  /** The overlay crossed the book and was left out; the caller should reset it. */
  overlayDiscarded: boolean;
}

/**
 * The display book: a snapshot plus the websocket add/cancel overlay, levels
 * merged and sorted. When the overlay makes the book cross (a stale local
 * delta), the overlay is dropped rather than shown (FE-16 crossed-book guard).
 */
export const composeOrderbook = (
  snapshot: GetOrderbook200Response | undefined,
  overlay: Overlay,
  instrument: string | undefined
): ComposedOrderbook => {
  if (!snapshot || !instrument) {
    return { book: snapshot, crossed: false, overlayDiscarded: false };
  }
  const build = (layer: Overlay) =>
    ({
      ...snapshot,
      instrument_name: instrument,
      bids: sumLevels(snapshot.bids, layer.bids).sort(
        (a, b) => Number(b[0]) - Number(a[0])
      ),
      asks: sumLevels(snapshot.asks, layer.asks).sort(
        (a, b) => Number(a[0]) - Number(b[0])
      ),
    } as GetOrderbook200Response);

  let book = build(overlay);
  let overlayDiscarded = false;
  if (hasOverlay(overlay) && isCrossedBook(book.bids, book.asks)) {
    book = build(newOverlay());
    overlayDiscarded = true;
  }
  return { book, crossed: isCrossedBook(book.bids, book.asks), overlayDiscarded };
};
